import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import PrivacyNotice from '../../legal/PrivacyNotice';
import { apiEndpoints, apiPublic } from '../../utils/apiService';

type PublicStore = {
  id: number;
  name: string;
  legal_name?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  logo_url?: string | null;
};

export default function OptInPage() {
  const { storeUuid } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState<PublicStore | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const [phone, setPhone] = useState<string>('');
  const [consentMsgs, setConsentMsgs] = useState<boolean>(false);
  const [permission, setPermission] = useState<{
    version: string;
    disclosure_token: string;
    sender_phone_number: string;
    text: string;
  } | null>(null);
  const [permissionFailed, setPermissionFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [submitSuccess, setSubmitSuccess] = useState<string>('');

  const fullAddress = useMemo(() => {
    if (!store) return '';
    const parts = [
      store.address_line_1,
      store.address_line_2,
      [store.city, store.state].filter(Boolean).join(', '),
      store.postal_code,
      store.country,
    ].filter(Boolean);
    return parts.join(' ');
  }, [store]);

  useEffect(() => {
    setConsentMsgs(false);
    setPermission(null);
    setPermissionFailed(false);
    if (!storeUuid) return;
    let active = true;
    fetch(
      `${process.env.REACT_APP_API_BASE_URL || ''}/api/privacy/sms-permission/${encodeURIComponent(storeUuid)}/`,
      { credentials: 'omit', referrerPolicy: 'no-referrer' },
    )
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (active) setPermission(data);
      })
      .catch(() => {
        if (active) {
          setPermissionFailed(true);
          setError(
            'The messaging sender could not be confirmed. Please try again later.',
          );
        }
      });
    return () => {
      active = false;
    };
  }, [storeUuid]);

  useEffect(() => {
    async function loadStore() {
      if (!storeUuid) return;
      setLoading(true);
      setError('');
      try {
        const res = await apiPublic.get(apiEndpoints.getPublicStore(storeUuid));
        if (res && res.ok) {
          const data = await res.json();
          setStore({
            id: data.id,
            name: data.name,
            legal_name: data.legal_name || '',
            address_line_1: data.address_line_1 || '',
            address_line_2: data.address_line_2 || '',
            city: data.city || '',
            state: data.state || '',
            postal_code: data.postal_code || '',
            country: data.country || '',
            logo_url: data.logo_url || null,
          });
        } else {
          setError('Could not load store info.');
        }
      } catch (e: any) {
        setError('Could not load store info.');
      } finally {
        setLoading(false);
      }
    }
    loadStore();
  }, [storeUuid]);

  const askArthurLogo =
    (process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}` : '') +
    '/images/logo/logo.svg';
  const logoSrc = store?.logo_url || askArthurLogo;
  const usingFallbackLogo = !store?.logo_url;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');
    if (!storeUuid) {
      setSubmitError('Invalid store.');
      return;
    }
    if (!consentMsgs) {
      setSubmitSuccess(
        'You have not opted in. No information was submitted and you can continue using the business’s services.',
      );
      return;
    }
    if (!permission) {
      setSubmitError('The messaging sender must be confirmed before opt-in.');
      return;
    }
    const digits = phone.replace(/\D/g, '');
    const normalized = '+' + (digits.length === 10 ? '1' + digits : digits);
    if (!/^\+1[0-9]{10}$/.test(normalized)) {
      setSubmitError(
        'Enter a US or Canadian phone number including area code.',
      );
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || ''}/api/privacy/sms-permission/${encodeURIComponent(storeUuid)}/`,
        {
          method: 'POST',
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone_number: normalized,
            consent_sms: true,
            disclosure_version: permission.version,
            disclosure_token: permission.disclosure_token,
            sender_phone_number: permission.sender_phone_number,
          }),
        },
      );
      const result = await response.json();
      if (!response.ok) {
        setSubmitError(
          typeof result.detail === 'string'
            ? result.detail
            : 'We could not record your permission. Please try again.',
        );
        return;
      }
      navigate(`/opt-in/${storeUuid}/success`, {
        state: { permissionRecorded: true },
      });
    } catch {
      setSubmitError('We could not record your permission. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-semibold">
              Welcome to {loading ? '...' : store?.name || 'Your Restaurant'}!
            </h1>
            <p className="mt-2 text-gray-600">
              Get SMS order updates and support messages from{' '}
              {loading ? '...' : store?.name || 'our restaurant'} via AskArthur.
            </p>
            <p className="text-gray-500 text-sm">
              No marketing. Just real-time updates on your orders.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow border border-gray-200 p-6 md:p-10">
            <div className="flex justify-center mb-6">
              <img
                src={logoSrc}
                alt={usingFallbackLogo ? 'AskArthur' : 'Store logo'}
                className={
                  usingFallbackLogo
                    ? 'h-7 md:h-8 w-auto object-contain'
                    : 'h-16 w-16 rounded-full object-contain'
                }
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).onerror = null;
                  (e.currentTarget as HTMLImageElement).src = askArthurLogo;
                }}
              />
            </div>

            <div className="rounded-xl bg-brand-500 text-white p-5 text-center mb-6">
              <p className="font-semibold">
                Please enter your phone number below if you would like to opt in
                to receiving order confirmations, ready-for-pickup alerts, and
                support messages from{' '}
                {loading ? '...' : store?.name || 'the restaurant'}, powered by
                AskArthur.
              </p>
            </div>

            {error && (
              <p role="alert" className="mb-4 text-red-700">
                {error}
              </p>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="sms-phone">Phone Number</Label>
                <Input
                  id="sms-phone"
                  type="tel"
                  name="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555-5555"
                  required={consentMsgs}
                />
              </div>

              <div className="space-y-3">
                {permission && (
                  <p className="text-sm">
                    Sender number: {permission.sender_phone_number}
                  </p>
                )}
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={consentMsgs}
                    disabled={permissionFailed}
                    onChange={(e) => setConsentMsgs(e.target.checked)}
                    className="mt-1 h-4 w-4"
                  />
                  <span className="text-gray-700">
                    {permission?.text ||
                      (permissionFailed
                        ? 'The SMS disclosure is unavailable, so opt-in cannot be recorded right now.'
                        : 'Loading sender-specific SMS disclosure…')}
                  </span>
                </label>
                <p className="text-sm">
                  Read the{' '}
                  <Link to="/legal/sms" className="underline">
                    SMS Terms
                  </Link>
                  .{' '}
                  <Link to="/legal/privacy" className="underline">
                    Privacy Policy
                  </Link>{' '}
                  is a notice about data use, not SMS permission.
                </p>
                <PrivacyNotice context="sms" />
              </div>

              {store && (
                <div className="text-xs text-gray-600 dark:text-gray-300">
                  {store.legal_name ? (
                    <div className="font-medium">{store.legal_name}</div>
                  ) : null}
                  <div className="font-medium">{store.name}</div>
                  {fullAddress && <div>{fullAddress}</div>}
                </div>
              )}

              {submitError && (
                <div className="p-3 text-sm text-error-600 bg-error-50 border border-error-200 rounded-lg">
                  {submitError}
                </div>
              )}
              {submitSuccess && (
                <div className="p-3 text-sm text-success-600 bg-success-50 border border-success-200 rounded-lg">
                  {submitSuccess}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || (consentMsgs && !permission)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg transition px-5 py-3.5 text-sm bg-brand-600 text-white shadow-theme-xs hover:bg-brand-700 disabled:opacity-60"
              >
                {submitting
                  ? 'Submitting...'
                  : consentMsgs
                    ? 'Save SMS permission'
                    : 'Continue without SMS'}
              </button>
            </form>
          </div>
        </div>
      </main>

      <footer className="py-10 text-gray-600 dark:text-gray-300 text-sm">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 px-4">
          <div>
            <div className="font-semibold mb-2">Sender</div>
            {store ? (
              <div className="space-y-1">
                {store.legal_name ? (
                  <div className="font-medium">{store.legal_name}</div>
                ) : null}
                <div>{store.name}</div>
                <div>
                  {[store.address_line_1, store.address_line_2]
                    .filter(Boolean)
                    .join(', ')}
                </div>
                <div>
                  {[store.city, store.state, store.postal_code, store.country]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              </div>
            ) : (
              <div className="text-gray-400">Loading…</div>
            )}
          </div>
          <div className="md:text-right">
            <div className="font-semibold mb-2">Powered by</div>
            <div>Ronin Tech LLC · AskArthur. All rights reserved.</div>
            <div>16192 Coastal Highway</div>
            <div>Lewes, DE 19958, United States</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
